import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Loader2,
  UserPlus,
  Mail,
  Phone,
  Link2,
  Trash2,
  X,
  Sparkles,
  Mic,
} from "lucide-react";
import TagInput from "@/components/TagInput";
import AdminVoiceFillModal from "@/components/AdminVoiceFillModal";

/**
 * Lightweight Kanban for the ATS pipeline.
 * Stages are columns; cards drag horizontally between them.
 * Clicking a card opens a side editor with notes + stage + skills.
 */

const STAGES = [
  { id: "new",        label: "New",        tone: "bg-clinical-bg text-clinical-text border-clinical-border" },
  { id: "screening",  label: "Screening",  tone: "bg-sky-50 text-sky-700 border-sky-200" },
  { id: "interview",  label: "Interview",  tone: "bg-violet-50 text-violet-700 border-violet-200" },
  { id: "offer",      label: "Offer",      tone: "bg-amber-50 text-amber-700 border-amber-200" },
  { id: "hired",      label: "Hired",      tone: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  { id: "rejected",   label: "Rejected",   tone: "bg-rose-50 text-rose-700 border-rose-200" },
];

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString([], { month: "short", day: "numeric" });
  } catch { return iso; }
};

export default function AdminApplicants() {
  const [applicants, setApplicants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [dragId, setDragId] = useState(null);
  const [hoverStage, setHoverStage] = useState(null);
  const [slug, setSlug] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/applicants");
      setApplicants(data || []);
    } catch (err) {
      toast.error("Could not load applicants");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    api.get("/agencies/me").then(({ data }) => setSlug(data.slug)).catch(() => {});
  }, []);

  const grouped = useMemo(() => {
    const out = Object.fromEntries(STAGES.map((s) => [s.id, []]));
    for (const a of applicants) {
      (out[a.stage] || (out["new"])).push(a);
    }
    return out;
  }, [applicants]);

  const moveTo = async (id, stage) => {
    const a = applicants.find((x) => x.id === id);
    if (!a || a.stage === stage) return;
    if (stage === "rejected") {
      // Open the editor so admin can fill reject_reason
      setEditing({ ...a, _pendingStage: "rejected" });
      return;
    }
    try {
      const { data } = await api.put(`/admin/applicants/${id}`, { stage });
      setApplicants((prev) => prev.map((x) => (x.id === id ? data : x)));
      toast.success(`Moved to ${stage}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Move failed");
    }
  };

  const copyPublicLink = () => {
    if (!slug) {
      toast.error("Agency slug not loaded yet");
      return;
    }
    const url = `${window.location.origin}/agency/${slug}/apply`;
    navigator.clipboard.writeText(url).then(
      () => toast.success("Public apply link copied"),
      () => toast.error("Copy failed — please copy manually")
    );
  };

  const convert = async (a) => {
    if (!window.confirm(`Send a caregiver invite to ${a.email}?`)) return;
    try {
      await api.post(`/admin/applicants/${a.id}/convert`);
      toast.success(`Invite emailed to ${a.name}`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Convert failed");
    }
  };

  if (loading && applicants.length === 0) {
    return (
      <div className="flex items-center justify-center py-20 text-clinical-subtext">
        <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading applicants…
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-applicants-page">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <div className="label-eyebrow">Hiring</div>
          <h1 className="mt-1 text-3xl font-heading font-semibold tracking-tight">Applicants</h1>
          <p className="mt-1 text-sm text-clinical-subtext">
            Track every applicant from first touch to hire. Drag a card to move it through your pipeline.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={copyPublicLink}
            className="inline-flex items-center gap-1.5 rounded-lg border border-clinical-border bg-white px-3 py-1.5 text-sm font-medium hover:bg-clinical-bg"
            data-testid="ats-public-link"
          >
            <Link2 className="h-3.5 w-3.5" /> Copy public apply link
          </button>
          <button
            type="button"
            onClick={() => setCreating(true)}
            className="btn-primary"
            data-testid="ats-add-applicant"
          >
            <UserPlus className="h-4 w-4" /> Add applicant
          </button>
        </div>
      </div>

      {/* Kanban */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="ats-kanban">
        {STAGES.map((s) => {
          const items = grouped[s.id] || [];
          const isOver = hoverStage === s.id && dragId;
          return (
            <div
              key={s.id}
              onDragOver={(e) => {
                if (!dragId) return;
                e.preventDefault();
                setHoverStage(s.id);
              }}
              onDragLeave={() => {
                if (hoverStage === s.id) setHoverStage(null);
              }}
              onDrop={(e) => {
                e.preventDefault();
                if (dragId) moveTo(dragId, s.id);
                setDragId(null);
                setHoverStage(null);
              }}
              className={`flex flex-col rounded-xl border bg-white min-h-[140px] transition-colors ${isOver ? "border-clinical-primary bg-clinical-primary/5" : "border-clinical-border"}`}
              data-testid={`ats-column-${s.id}`}
            >
              <div className="px-3 py-2 border-b border-clinical-border flex items-center justify-between">
                <span className={`text-[11px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded border ${s.tone}`}>
                  {s.label}
                </span>
                <span className="text-[11px] text-clinical-subtext">{items.length}</span>
              </div>
              <div className="flex-1 p-2 space-y-2">
                {items.length === 0 && (
                  <p className="text-[11px] text-clinical-subtext italic px-1 py-2">Nothing here yet.</p>
                )}
                {items.map((a) => (
                  <button
                    key={a.id}
                    type="button"
                    draggable
                    onDragStart={() => setDragId(a.id)}
                    onDragEnd={() => { setDragId(null); setHoverStage(null); }}
                    onClick={() => setEditing(a)}
                    className={`w-full text-left rounded-lg border border-clinical-border bg-white p-2.5 hover:border-clinical-primary/50 transition-all cursor-grab active:cursor-grabbing ${dragId === a.id ? "opacity-40" : ""}`}
                    data-testid={`ats-card-${a.id}`}
                  >
                    <div className="text-sm font-medium text-clinical-text">{a.name}</div>
                    <div className="mt-0.5 flex items-center gap-1.5 text-[11px] text-clinical-subtext">
                      <Mail className="h-3 w-3" />
                      <span className="truncate">{a.email}</span>
                    </div>
                    {a.phone && (
                      <div className="mt-0.5 flex items-center gap-1.5 text-[11px] text-clinical-subtext">
                        <Phone className="h-3 w-3" /> {a.phone}
                      </div>
                    )}
                    {(a.skills?.length || 0) > 0 && (
                      <div className="mt-1.5 flex flex-wrap gap-1">
                        {a.skills.slice(0, 3).map((sk) => (
                          <span key={sk} className="inline-block text-[10px] bg-clinical-bg border border-clinical-border rounded px-1 py-0.5">{sk}</span>
                        ))}
                        {a.skills.length > 3 && (
                          <span className="text-[10px] text-clinical-subtext">+{a.skills.length - 3}</span>
                        )}
                      </div>
                    )}
                    <div className="mt-1.5 flex items-center justify-between text-[10px] text-clinical-subtext">
                      <span>Applied {fmtDate(a.created_at)}</span>
                      {a.source && <span className="italic">{a.source}</span>}
                    </div>
                    {s.id === "hired" && !a.converted_user_id && (
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); convert(a); }}
                        className="mt-2 w-full inline-flex items-center justify-center gap-1 rounded bg-emerald-600 text-white text-[11px] font-medium py-1 hover:bg-emerald-700"
                        data-testid={`ats-convert-${a.id}`}
                      >
                        <Sparkles className="h-3 w-3" /> Send caregiver invite
                      </button>
                    )}
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {(editing || creating) && (
        <ApplicantEditor
          applicant={editing}
          onClose={() => { setEditing(null); setCreating(false); }}
          onSaved={(saved, deleted) => {
            if (deleted) {
              setApplicants((prev) => prev.filter((x) => x.id !== saved.id));
            } else if (editing) {
              setApplicants((prev) => prev.map((x) => (x.id === saved.id ? saved : x)));
            } else {
              setApplicants((prev) => [saved, ...prev]);
            }
            setEditing(null);
            setCreating(false);
          }}
        />
      )}
    </div>
  );
}

function ApplicantEditor({ applicant, onClose, onSaved }) {
  const isNew = !applicant;
  const [form, setForm] = useState(() => ({
    name: applicant?.name || "",
    email: applicant?.email || "",
    phone: applicant?.phone || "",
    stage: applicant?._pendingStage || applicant?.stage || "new",
    source: applicant?.source || "",
    skills: applicant?.skills || [],
    languages: applicant?.languages || [],
    notes: applicant?.notes || "",
    reject_reason: applicant?.reject_reason || "",
    interview_at: applicant?.interview_at?.slice(0, 16) || "",
  }));
  const [saving, setSaving] = useState(false);
  const [voiceOpen, setVoiceOpen] = useState(false);

  const save = async () => {
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Name and email are required");
      return;
    }
    if (form.stage === "rejected" && !form.reject_reason.trim()) {
      toast.error("A reject reason is required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        phone: form.phone.trim(),
        stage: form.stage,
        source: form.source.trim(),
        skills: form.skills,
        languages: form.languages,
        notes: form.notes,
        reject_reason: form.reject_reason,
        interview_at: form.interview_at ? new Date(form.interview_at).toISOString() : null,
      };
      const { data } = isNew
        ? await api.post("/admin/applicants", payload)
        : await api.put(`/admin/applicants/${applicant.id}`, payload);
      toast.success(isNew ? "Applicant added" : "Saved");
      onSaved(data, false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!applicant) return;
    if (!window.confirm(`Delete ${applicant.name}'s application?`)) return;
    try {
      await api.delete(`/admin/applicants/${applicant.id}`);
      toast.success("Deleted");
      onSaved(applicant, true);
    } catch (err) {
      toast.error("Delete failed");
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm" onClick={onClose} data-testid="ats-editor">
      <div className="bg-white rounded-2xl border border-clinical-border shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-clinical-border">
          <h2 className="font-heading text-lg font-medium">
            {isNew ? "Add applicant" : applicant.name}
          </h2>
          <button onClick={onClose} className="text-clinical-subtext hover:text-clinical-text">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="p-5 space-y-3 text-sm">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Name">
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input-clinical" data-testid="ats-name" />
            </Field>
            <Field label="Email">
              <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="input-clinical" data-testid="ats-email" />
            </Field>
            <Field label="Phone">
              <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="input-clinical" />
            </Field>
            <Field label="Source">
              <input value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} placeholder="Indeed, Referral…" className="input-clinical" />
            </Field>
            <Field label="Stage">
              <select value={form.stage} onChange={(e) => setForm({ ...form, stage: e.target.value })} className="input-clinical" data-testid="ats-stage">
                {STAGES.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
              </select>
            </Field>
            <Field label="Interview at">
              <input type="datetime-local" value={form.interview_at} onChange={(e) => setForm({ ...form, interview_at: e.target.value })} className="input-clinical" />
            </Field>
          </div>
          <Field label="Skills">
            <TagInput value={form.skills} onChange={(v) => setForm({ ...form, skills: v })} suggestions={["dementia-care","hoyer-lift","cpr","first-aid","wound-care"]} placeholder="dementia-care, cpr…" />
          </Field>
          <Field label="Languages">
            <TagInput value={form.languages} onChange={(v) => setForm({ ...form, languages: v })} suggestions={["en","es","fr","ar","pt","tl","zh"]} placeholder="en, es…" />
          </Field>
          <Field label={
            <div className="flex items-center justify-between w-full">
              <span>Notes</span>
              <button
                type="button"
                onClick={() => setVoiceOpen(true)}
                className="inline-flex items-center gap-1 rounded-lg bg-purple-100 px-2 py-1 text-[11px] font-semibold text-purple-700 hover:bg-purple-200"
                data-testid="ats-voice-note-btn"
              >
                <Mic size={11} /> Voice note
              </button>
            </div>
          }>
            <textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="input-clinical" data-testid="ats-notes" />
          </Field>
          <AdminVoiceFillModal
            open={voiceOpen}
            onClose={() => setVoiceOpen(false)}
            endpoint="/ai/voice-fill/applicant-note"
            title="Voice interview feedback"
            hint='e.g. "Strong dementia care experience, warm with families. Slight concern about availability on weekends. Recommend a second interview. 8 out of 10."'
            examples={[
              "Strong dementia experience, warm with families. Slight concern about weekends. Recommend a second interview. 8 out of 10.",
              "Hard pass — could not answer basic transfer questions. Reject.",
              "Loved this candidate. Hire them on the spot. Reference check first.",
            ]}
            limitMs={90 * 1000}
            filenamePrefix="atsnote"
            testIdPrefix="ats-voice"
            renderPreview={(r) => (
              <div className="space-y-2 text-sm">
                {r.summary && (
                  <div>
                    <div className="label-eyebrow">Summary</div>
                    <p className="text-clinical-text">{r.summary}</p>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-3">
                  {(r.pros || []).length > 0 && (
                    <div>
                      <div className="label-eyebrow text-green-700">Pros</div>
                      <ul className="list-disc pl-4 text-xs text-clinical-text">
                        {r.pros.map((p, i) => (<li key={i}>{p}</li>))}
                      </ul>
                    </div>
                  )}
                  {(r.cons || []).length > 0 && (
                    <div>
                      <div className="label-eyebrow text-rose-700">Cons</div>
                      <ul className="list-disc pl-4 text-xs text-clinical-text">
                        {r.cons.map((p, i) => (<li key={i}>{p}</li>))}
                      </ul>
                    </div>
                  )}
                </div>
                <div className="flex flex-wrap gap-2 text-xs">
                  {r.score != null && (<span className="pill bg-clinical-bg">Score: {r.score}/10</span>)}
                  {r.decision && (<span className="pill bg-violet-100 text-violet-700">{r.decision.replace(/_/g, " ")}</span>)}
                  {r.next_step && (<span className="pill bg-amber-100 text-amber-900">Next: {r.next_step}</span>)}
                  {(r.skill_tags || []).map((t) => (<span key={t} className="pill bg-clinical-bg">{t}</span>))}
                </div>
              </div>
            )}
            onConfirm={(r) => {
              const stamp = new Date().toISOString().slice(0, 10);
              const block = [
                `[${stamp}] ${r.summary || r.transcript}`,
                (r.pros || []).length ? `Pros: ${r.pros.join("; ")}` : "",
                (r.cons || []).length ? `Cons: ${r.cons.join("; ")}` : "",
                r.score != null ? `Score: ${r.score}/10` : "",
                r.decision ? `Decision: ${r.decision}` : "",
                r.next_step ? `Next step: ${r.next_step}` : "",
              ].filter(Boolean).join("\n");
              const merged = { ...form };
              merged.notes = form.notes ? `${form.notes}\n\n${block}` : block;
              if (r.skill_tags && r.skill_tags.length) {
                const merged_skills = Array.from(new Set([...(form.skills || []), ...r.skill_tags]));
                merged.skills = merged_skills;
              }
              if (r.decision === "advance" || r.decision === "second_interview") {
                merged.stage = "interview";
              } else if (r.decision === "reject") {
                merged.stage = "rejected";
              } else if (r.decision === "hold") {
                merged.stage = "screening";
              }
              setForm(merged);
              toast.success("Interview note added");
            }}
            confirmLabel="Append to notes"
          />
          {form.stage === "rejected" && (
            <Field label="Reject reason (required)">
              <textarea rows={2} value={form.reject_reason} onChange={(e) => setForm({ ...form, reject_reason: e.target.value })} className="input-clinical" data-testid="ats-reject-reason" />
            </Field>
          )}
        </div>

        <div className="px-5 py-4 border-t border-clinical-border flex items-center justify-between gap-2">
          {!isNew ? (
            <button onClick={remove} className="btn-ghost !text-destructive" data-testid="ats-delete">
              <Trash2 className="h-4 w-4" /> Delete
            </button>
          ) : <span />}
          <div className="flex items-center gap-2">
            <button onClick={onClose} className="text-sm text-clinical-subtext px-3 py-1.5">Cancel</button>
            <button onClick={save} disabled={saving} className="btn-primary" data-testid="ats-save">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (isNew ? "Add applicant" : "Save")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="label-eyebrow mb-1 block">{label}</label>
      {children}
    </div>
  );
}
